/**
 * Where the app considers "home" to be, per breakpoint.
 *
 * Directive-free and shared, because two things now answer this question and
 * they must never drift: `RootLandingRedirect` sends `/` here, and the updater
 * applies a pending version when somebody arrives here (`update-apply.ts`).
 * A second copy of the list would mean the update pinning itself to a page the
 * product no longer treats as home — which fails by never firing, silently.
 *
 * On a computer, home can be changed: Settings → You → Display → Start page.
 * It is kept per computer (an office manager who lives in Quotes at the desk
 * may still want the Diary on the laptop), and it is only ever honoured when
 * the person can open that page in the business they are in now — a second
 * business without Quotes, or a teammate being viewed as, falls back to the
 * Diary rather than landing on a page that refuses them.
 */
export const PHONE_LANDING_PATH = '/overview';
export const DESKTOP_LANDING_PATH = '/schedule';

export const START_PAGE_KEY = 'mucka_start_page';

export interface StartPage {
  path: string;
  /** As the side menu names it, so the choice reads like the place it opens. */
  label: string;
  /** Any one of these opens it — the same keys the side menu shows it on. */
  permission: readonly string[];
  /** A skill page also needs the skill switched on for the business. */
  skill?: string;
}

/** The pages a computer can open on. Diary first: it is the default. */
export const START_PAGES: readonly StartPage[] = [
  { path: DESKTOP_LANDING_PATH, label: 'Schedule', permission: ['view_schedule'] },
  { path: '/actions', label: 'Actions', permission: ['view_actions'] },
  { path: '/jobs', label: 'Live Jobs', permission: ['view_all_jobs', 'view_own_jobs'] },
  { path: '/clients', label: 'Contacts', permission: ['view_clients'] },
  // No `skill`: Quotes and Invoicing are part of every account now
  // (src/lib/skills/core.ts), so the permission is the whole gate.
  { path: '/quotes', label: 'Quotes', permission: ['view_quotes'] },
  { path: '/invoices', label: 'Invoicing', permission: ['view_invoices'] },
];

/** The start pages this person can open, in the business they are in. */
export function startPagesFor(
  perms: Record<string, boolean> | null | undefined,
  enabledSkillSlugs: readonly string[] | null | undefined,
): StartPage[] {
  return START_PAGES.filter(p =>
    p.permission.some(k => perms?.[k] === true)
    && (!p.skill || (enabledSkillSlugs ?? []).includes(p.skill)));
}

/** This computer's chosen start page, or null for the default. */
export function readStartPage(): string | null {
  try {
    const v = localStorage.getItem(START_PAGE_KEY);
    return v && START_PAGES.some(p => p.path === v) ? v : null;
  } catch {
    return null;
  }
}

/** Remember a start page on this computer; the default (or null) forgets it. */
export function setStartPage(path: string | null): void {
  try {
    if (!path || path === DESKTOP_LANDING_PATH) localStorage.removeItem(START_PAGE_KEY);
    else localStorage.setItem(START_PAGE_KEY, path);
  } catch {
    // Blocked storage: it stays the default, which is what they had.
  }
}

/**
 * Home for this device. `canOpen` is the gate on a chosen start page: pass it
 * wherever somebody is about to be SENT there. Leave it out only where the
 * question is "is this the page they treat as home?" (the updater), which
 * somebody already standing on it has answered.
 */
export function landingPathFor(isPhone: boolean, canOpen?: (path: string) => boolean): string {
  if (isPhone) return PHONE_LANDING_PATH;
  const chosen = readStartPage();
  if (chosen && (!canOpen || canOpen(chosen))) return chosen;
  return DESKTOP_LANDING_PATH;
}
